import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { MenuService } from '../../services/permission-group/permission-group.service';
import { getDesktopShortcuts, getAvailableMobileModules, getUserMobileModules } from '../../services/user/user.service';
import { SystemSettingsService } from '../../services/systemSettings/systemSettings.service';

export const fetchMenus = createAsyncThunk(
  'menu/fetchMenus',
  async () => {
    const data = await MenuService.getAll();
    return data;
  }
);

export const fetchDesktopShortcuts = createAsyncThunk(
  'menu/fetchDesktopShortcuts',
  async () => {
    const data = await getDesktopShortcuts();
    return data.map(item => item.screen_key);
  }
);

export const fetchMobileModulesAvailable = createAsyncThunk(
  'menu/fetchMobileModulesAvailable',
  async () => {
    const data = await getAvailableMobileModules();
    return data;
  }
);

export const fetchMobileModulesUser = createAsyncThunk(
  'menu/fetchMobileModulesUser',
  async () => {
    const data = await getUserMobileModules();
    return data.map(m => m.module_key);
  }
);

export const fetchUseStaticMenu = createAsyncThunk(
  'menu/fetchUseStaticMenu',
  async () => {
    const value = await SystemSettingsService.getPublicValue('desktop.use_static_menu');
    return value === true || value === '1' || value === 1;
  }
);

export const fetchShowSupportTicket = createAsyncThunk(
  'menu/fetchShowSupportTicket',
  async () => {
    const value = await SystemSettingsService.getPublicValue('desktop.show_support_ticket');
    return value === true || value === '1' || value === 1;
  }
);

export const fetchKeyboardShortcutsEnabled = createAsyncThunk(
  'menu/fetchKeyboardShortcutsEnabled',
  async () => {
    const value = await SystemSettingsService.getPublicValue('desktop.keyboard_shortcuts.enabled');
    return value === true || value === '1' || value === 1;
  }
);

const initialState = {
  menus: [],
  menusLoaded: false,
  desktopShortcutKeys: [],
  desktopShortcutsLoaded: false,
  mobileModules: [],
  mobileMaxShortcuts: 3,
  mobileModulesLoaded: false,
  mobileSelectedKeys: [],
  mobileSelectedLoaded: false,
  useStaticMenu: false,
  useStaticMenuLoaded: false,
  showSupportTicket: true,
  showSupportTicketLoaded: false,
  keyboardShortcutsEnabled: false,
  keyboardShortcutsLoaded: false,
};

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    setDesktopShortcutKeys: (state, { payload }) => {
      state.desktopShortcutKeys = payload;
    },
    setMobileSelectedKeys: (state, { payload }) => {
      state.mobileSelectedKeys = payload;
    },
    invalidateSettings: (state) => {
      state.useStaticMenuLoaded = false;
      state.showSupportTicketLoaded = false;
      state.keyboardShortcutsLoaded = false;
      state.menusLoaded = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchMenus.fulfilled, (state, { payload }) => {
      state.menus = payload;
      state.menusLoaded = true;
    });
    builder.addCase(fetchMenus.rejected, (state) => {
      state.menusLoaded = true;
    });

    builder.addCase(fetchDesktopShortcuts.fulfilled, (state, { payload }) => {
      state.desktopShortcutKeys = payload;
      state.desktopShortcutsLoaded = true;
    });
    builder.addCase(fetchDesktopShortcuts.rejected, (state) => {
      state.desktopShortcutsLoaded = true;
    });

    builder.addCase(fetchMobileModulesAvailable.fulfilled, (state, { payload }) => {
      if (payload?.modules) {
        state.mobileModules = payload.modules;
      }
      if (payload?.max_shortcuts) {
        state.mobileMaxShortcuts = payload.max_shortcuts;
      }
      state.mobileModulesLoaded = true;
    });
    builder.addCase(fetchMobileModulesAvailable.rejected, (state) => {
      state.mobileModulesLoaded = true;
    });

    builder.addCase(fetchMobileModulesUser.fulfilled, (state, { payload }) => {
      state.mobileSelectedKeys = payload;
      state.mobileSelectedLoaded = true;
    });
    builder.addCase(fetchMobileModulesUser.rejected, (state) => {
      state.mobileSelectedLoaded = true;
    });

    builder.addCase(fetchUseStaticMenu.fulfilled, (state, { payload }) => {
      state.useStaticMenu = payload;
      state.useStaticMenuLoaded = true;
    });
    builder.addCase(fetchUseStaticMenu.rejected, (state) => {
      state.useStaticMenu = false;
      state.useStaticMenuLoaded = true;
    });

    builder.addCase(fetchShowSupportTicket.fulfilled, (state, { payload }) => {
      state.showSupportTicket = payload;
      state.showSupportTicketLoaded = true;
    });
    builder.addCase(fetchShowSupportTicket.rejected, (state) => {
      state.showSupportTicket = false;
      state.showSupportTicketLoaded = true;
    });

    builder.addCase(fetchKeyboardShortcutsEnabled.fulfilled, (state, { payload }) => {
      state.keyboardShortcutsEnabled = payload;
      state.keyboardShortcutsLoaded = true;
    });
    builder.addCase(fetchKeyboardShortcutsEnabled.rejected, (state) => {
      state.keyboardShortcutsEnabled = false;
      state.keyboardShortcutsLoaded = true;
    });
  },
});

export const { setDesktopShortcutKeys, setMobileSelectedKeys, invalidateSettings } = menuSlice.actions;
export default menuSlice.reducer;
